/**
 * @file Shared parent-board card view helpers.
 *
 * Domain card adapters own data lookup, title/subhead rules, date resolution,
 * quick actions, and embedded content. This module owns reusable board-card
 * shell markup and slot composition.
 */

import {
    escapeHTML,
} from "../utils/contentUtils.js";
import {
    isColorDark,
} from "../utils/displayUtils.js";

/**
 * Formats a card date as dd/mm/yy.
 * @param {string|null|undefined} dateValue - Date-like value to format.
 * @returns {string} Formatted date or an empty string.
 */
export function formatBoardCardDate(dateValue) {
  if (!dateValue) {
    return "";
  }

  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }

  const day = String(parsedDate.getDate()).padStart(2, "0");
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const year = String(parsedDate.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
}

/**
 * Formats a card date as dd/mm for compact responsive slots.
 * @param {string|null|undefined} dateValue - Date-like value to format.
 * @returns {string} Compact formatted date or an empty string.
 */
export function formatBoardCardDateCompact(dateValue) {
  if (!dateValue) {
    return "";
  }

  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }

  const day = String(parsedDate.getDate()).padStart(2, "0");
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}`;
}

/**
 * Renders the type chip lane for a board card.
 * @param {Array<string>} typeIds - Selected type ids.
 * @param {Array<object>} typeConfig - Available type config entries.
 * @param {object} options - Type chip rendering options.
 * @param {boolean} [options.expandToLane] - Whether the chip lane may flex.
 * @param {string} [options.overflowLabel] - Label for hidden type overflow.
 * @param {boolean} [options.singleOnly] - Whether to render only the first type.
 * @param {number} [options.maxEstimatedUnits] - Estimated visible text budget.
 * @returns {string} Type chip HTML.
 */
export function renderBoardCardTypeChip(typeIds = [], typeConfig = [], options = {}) {
  if (!Array.isArray(typeIds) || typeIds.length === 0) {
    return "";
  }

  const resolvedTypeNames = typeIds.map((typeId) =>
    typeConfig.find((type) => type.id === typeId)?.name || typeId
  );
  const chipOverflowLabel = options.overflowLabel || "...";
  const containerClass = options.expandToLane ?
    "flex max-w-full min-w-0 flex-1 items-center gap-1" :
    "inline-flex items-center gap-1 min-w-0";
  const chipsContainerClass = options.expandToLane ?
    "flex max-w-full min-w-0 shrink items-center gap-1 overflow-hidden" :
    "inline-flex items-center gap-1 min-w-0 overflow-hidden";

  if (options.singleOnly) {
    const primaryType = typeConfig.find((type) => type.id === typeIds[0]);
    if (!primaryType) {
      return "";
    }

    const typeName = primaryType.name || typeIds[0];
    const chipTextColor = isColorDark(primaryType.color) ?
      "#ffffff" :
      "#111827";

    return `
            <span class="${containerClass}" title="${escapeHTML(resolvedTypeNames.join("\n"))}">
                <span class="inline-flex items-center max-w-full h-5 px-1.5 rounded text-[10px] font-medium truncate" style="background-color: ${primaryType.color || "#e5e7eb"}; color: ${chipTextColor};">
                    ${escapeHTML(typeName)}
                </span>
                ${resolvedTypeNames.length > 1 ? `<span class="inline-flex items-center justify-center h-5 px-0.5 text-[11px] font-medium text-gray-500 dark:text-gray-300">${escapeHTML(chipOverflowLabel)}</span>` : ""}
            </span>
        `;
  }

  const maxEstimatedUnits = options.maxEstimatedUnits ?? 22;
  let usedUnits = 0;
  const visibleTypeEntries = [];

  resolvedTypeNames.forEach((typeName, index) => {
    const typeOption = typeConfig.find((type) => type.id === typeIds[index]);
    if (!typeOption) {
      return;
    }

    const estimatedUnits = Math.max(typeName.length + 4, 8);
    if (visibleTypeEntries.length > 0 && (usedUnits + estimatedUnits) > maxEstimatedUnits) {
      return;
    }

    visibleTypeEntries.push({
      name: typeName,
      color: typeOption.color || "#e5e7eb",
    });
    usedUnits += estimatedUnits;
  });

  if (visibleTypeEntries.length === 0) {
    const fallbackType = typeConfig.find((type) => type.id === typeIds[0]);
    if (!fallbackType) {
      return "";
    }
    visibleTypeEntries.push({
      name: fallbackType.name || typeIds[0],
      color: fallbackType.color || "#e5e7eb",
    });
  }

  const hiddenCount = resolvedTypeNames.length - visibleTypeEntries.length;
  const chipsHtml = visibleTypeEntries.map((typeEntry) => {
    const chipTextColor = isColorDark(typeEntry.color) ? "#ffffff" : "#111827";

    return `
            <span class="inline-flex items-center max-w-full h-5 px-1.5 rounded text-[10px] font-medium truncate" style="background-color: ${typeEntry.color}; color: ${chipTextColor};">
                ${escapeHTML(typeEntry.name || "")}
            </span>
        `;
  }).join("");

  return `
        <span class="${containerClass}" title="${escapeHTML(resolvedTypeNames.join("\n"))}">
            <span class="${chipsContainerClass}">
                ${chipsHtml}
            </span>
            ${hiddenCount > 0 ? `<span class="inline-flex items-center justify-center h-5 px-0.5 text-[11px] font-medium text-gray-500 dark:text-gray-300">${escapeHTML(chipOverflowLabel)}</span>` : ""}
        </span>
    `;
}

/**
 * Renders a status chip for a board card.
 * @param {object} status - Resolved status config.
 * @returns {string} Status chip HTML.
 */
export function renderBoardCardStatusChip(status = {}) {
  const statusColor = status.color || "#e5e7eb";
  const statusTextColor = isColorDark(statusColor) ? "#ffffff" : "#111827";

  return `
        <span class="inline-flex items-center h-5 px-1.5 rounded text-[10px] font-medium whitespace-nowrap" style="background-color: ${statusColor}; color: ${statusTextColor};" title="${escapeHTML(status.name || "N/A")}">
            ${escapeHTML(status.name || "N/A")}
        </span>
    `;
}

/**
 * Renders the shared card info row.
 * @param {object} options - Info row slot options.
 * @param {string} [options.statusChipHtml] - Status chip slot.
 * @param {string} [options.typeChipHtml] - Type chip slot.
 * @param {string} [options.dateHtml] - Date slot.
 * @param {string} [options.userHtml] - User slot.
 * @param {boolean} [options.showUserSlot] - Whether to reserve user space.
 * @param {boolean} [options.hasEmbeddedContent] - Whether embedded content follows.
 * @param {string} [options.infoRowGridClassOverride] - Explicit grid class.
 * @returns {string} Info row HTML.
 */
export function renderBoardCardInfoRow({
  statusChipHtml = "",
  typeChipHtml = "",
  dateHtml = "",
  userHtml = "",
  showUserSlot = true,
  hasEmbeddedContent = false,
  infoRowGridClassOverride = "",
}) {
  const hasDisplayDate = !!dateHtml;
  let infoRowGridClass = infoRowGridClassOverride || "card-info-row--base";
  let leftLaneClass = "card-info-row__left";

  if (!infoRowGridClassOverride && showUserSlot && hasDisplayDate) {
    infoRowGridClass = "card-info-row--with-user-date";
  } else if (!infoRowGridClassOverride && showUserSlot && !hasDisplayDate) {
    infoRowGridClass = "card-info-row--with-user";
    leftLaneClass = "card-info-row__left card-info-row__left--expanded";
  } else if (!infoRowGridClassOverride && !showUserSlot && hasDisplayDate) {
    infoRowGridClass = "card-info-row--date-only";
  }

  return `
        <div class="card-info-row ${infoRowGridClass} items-center gap-x-[clamp(0.2rem,0.7vw,0.5rem)] text-sm text-gray-500 dark:text-gray-400 min-w-0 ${hasEmbeddedContent ? "mb-2 pb-2 border-b dark:border-gray-600" : ""}">
            <div class="${leftLaneClass}">
                ${statusChipHtml}
                ${typeChipHtml}
            </div>
            ${dateHtml}
            ${showUserSlot ? userHtml : ""}
        </div>
    `;
}

/**
 * Renders the outer board-card shell.
 * @param {object} options - Card shell slots.
 * @param {string} options.itemId - Item id for card actions.
 * @param {string} options.borderColor - Left border color.
 * @param {string} options.titleHtml - Primary title slot.
 * @param {string} [options.secondaryTitleHtml] - Secondary title slot.
 * @param {string} [options.infoRowHtml] - Info row slot.
 * @param {string} [options.embeddedContentHtml] - Embedded content slot.
 * @param {string} [options.checklistHtml] - Checklist preview slot.
 * @param {string} [options.actionButtonHtml] - Action button slot.
 * @returns {string} Board-card shell HTML.
 */
export function renderBoardCardShell({
  itemId,
  borderColor,
  titleHtml,
  secondaryTitleHtml = "",
  infoRowHtml = "",
  embeddedContentHtml = "",
  checklistHtml = "",
  actionButtonHtml = "",
}) {
  return `
        <div data-id="${itemId}" draggable="true" class="kanban-card relative group bg-gray-50 p-3 rounded-md border border-slate-100 border-l-4 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600 dark:border-gray-600" style="border-left-color: ${borderColor};">
            ${titleHtml}
            ${secondaryTitleHtml}
            ${infoRowHtml}
            ${embeddedContentHtml}
            ${checklistHtml}
            ${actionButtonHtml}
        </div>
    `;
}

/**
 * Renders a responsive date slot for a board card.
 * @param {object} options - Date slot options.
 * @param {string} options.dateText - Full date text.
 * @param {string} options.compactDateText - Compact date text.
 * @param {string} options.tooltip - Date tooltip.
 * @param {boolean} [options.alignRight] - Whether the date aligns right.
 * @returns {string} Date slot HTML.
 */
export function renderBoardCardDateSlot({
  dateText,
  compactDateText,
  tooltip,
  alignRight = false,
}) {
  if (!dateText) {
    return "";
  }

  return `<span class="${alignRight ? "card-info-row__date card-info-row__date--right" : "card-info-row__date"}" title="${escapeHTML(tooltip || "")}">
                    <span class="hidden min-[1340px]:inline">${dateText}</span>
                    <span class="min-[1340px]:hidden">${compactDateText}</span>
                </span>`;
}

/**
 * Renders a user/owner initials slot for a board card.
 * @param {object} options - User slot options.
 * @param {string} options.text - Visible user text.
 * @param {string} options.title - Tooltip text.
 * @returns {string} User slot HTML.
 */
export function renderBoardCardUserSlot({ text = "", title = "" }) {
  return `<span class="card-info-row__user" title="${escapeHTML(title || "")}">${escapeHTML(text || "")}</span>`;
}

/**
 * Renders unchecked checklist preview rows for a board card.
 * @param {object} item - Board item with optional checklist map.
 * @param {string} itemType - Checklist item type for the toggle handler.
 * @param {object} options - Checklist preview options.
 * @param {number} [options.limit] - Maximum visible unchecked items.
 * @returns {string} Checklist preview HTML.
 */
export function renderBoardCardChecklistPreview(item, itemType, { limit = 1 } = {}) {
  if (!item?.checklist || typeof item.checklist !== "object") {
    return "";
  }

  const checklistItems = Object.entries(item.checklist)
    .map(([id, data]) => ({ id, ...data }))
    .filter((checklistItem) => !checklistItem.isCompleted)
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .slice(0, limit);

  if (checklistItems.length === 0) {
    return "";
  }

  const itemsHtml = checklistItems.map((checklistItem) => `
        <div class="flex items-center group">
            <button type="button" class="mr-1 inline-flex h-3 w-3 flex-shrink-0 items-center justify-center rounded-[3px] border border-gray-400 bg-transparent text-transparent transition-colors hover:border-gray-500" onclick="window.handleKanbanChecklistToggle(event, '${item.id}', '${itemType}', '${checklistItem.id}')" title="Mark Complete">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-2.5 w-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.25">
                    <path class="checklist-toggle-tick opacity-0 transition-opacity" stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"></path>
                </svg>
            </button>
            <span class="text-xs text-gray-500 dark:text-gray-400 truncate flex-grow" title="${escapeHTML(checklistItem.text || "")}">${escapeHTML(checklistItem.text || "")}</span>
        </div>
    `).join("");

  return `
        <div class="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600 space-y-1">
            ${itemsHtml}
        </div>
    `;
}

/**
 * Renders the shared parent-board card structure.
 * @param {object} options - Parent-card slots and resolved metadata.
 * @param {string} options.itemId - Parent item id.
 * @param {string} options.itemType - Parent item type label for fallback text.
 * @param {string} options.titleText - Resolved visible title.
 * @param {string} options.borderColor - Left border color.
 * @param {string} options.statusChipHtml - Status chip slot.
 * @param {string} options.typeChipHtml - Type chip slot.
 * @param {string} options.dateText - Full display date.
 * @param {string} options.compactDateText - Compact display date.
 * @param {string} options.dateTooltip - Date tooltip text.
 * @param {object} options.userDisplay - User initials and tooltip.
 * @param {boolean} options.showUserSlot - Whether to show the user slot.
 * @param {string} [options.embeddedContentHtml] - Embedded content slot.
 * @param {string} [options.checklistHtml] - Checklist preview slot.
 * @param {string} [options.actionButtonHtml] - Action button slot.
 * @returns {string} Parent-board card HTML.
 */
export function renderParentBoardCard({
  itemId,
  itemType,
  titleText,
  borderColor,
  statusChipHtml,
  typeChipHtml,
  dateText,
  compactDateText,
  dateTooltip,
  userDisplay,
  showUserSlot,
  embeddedContentHtml = "",
  checklistHtml = "",
  actionButtonHtml = "",
}) {
  if (!itemId) {
    return "<div class=\"kanban-card bg-red-100 p-3 rounded-md border-l-4 border-red-500 text-xs\">Invalid Card Data</div>";
  }

  const hasEmbeddedContent = embeddedContentHtml.trim().length > 0;
  const infoRowHtml = renderBoardCardInfoRow({
    statusChipHtml,
    typeChipHtml,
    dateHtml: renderBoardCardDateSlot({
      dateText,
      compactDateText,
      tooltip: dateTooltip,
      alignRight: !showUserSlot,
    }),
    userHtml: renderBoardCardUserSlot(userDisplay || {}),
    showUserSlot,
    hasEmbeddedContent,
  });
  const fallbackTitle = `Unnamed ${itemType}`;
  const cardTitle = titleText || fallbackTitle;

  return renderBoardCardShell({
    itemId,
    borderColor,
    titleHtml: `<p class="font-semibold text-sm mb-2 truncate dark:text-gray-200" title="${escapeHTML(titleText || "")}">${escapeHTML(cardTitle)}</p>`,
    infoRowHtml,
    embeddedContentHtml,
    checklistHtml,
    actionButtonHtml,
  });
}
